import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

interface AntibodyDragState {
  /* The viewbox x-coordinate of the top left corner of the antibody preview. */
  x: number;
  /* The viewbox y-coordinate of the top left corner of the antibody preview. */
  y: number;
  /*
   * The difference in x-coordinates between the location of the click that initiated a drag and the left side
   * of the bounding box. Should be `DEFAULT_ANTIBODY_DRAG_COORDINATE` if there is no current drag.
   */
  offsetX: number;
  /*
   * The difference in y-coordinates between the location of the click that initiated a drag and the top side
   * of the bounding box. Should be `DEFAULT_ANTIBODY_DRAG_COORDINATE` if there is no current drag.
   */
  offsetY: number;
  /* Whether the antibody viz is currently being dragged. */
  isDragging: boolean;
}

export interface AntibodyMapState {
  /*
   * When the domain preview is expanded, the antibody preview is scaled down and its div is made draggable.
   * Data to do with the location of the draggable div lives in this struct.
   */
  dragState: AntibodyDragState;
  /* Whether the preview of the selected domain is expanded to fill all vertical space available. */
  expandPreview: boolean;
  /* Which domain is currently hovered, if any */
  hoveredRef: number | null;
  /* Which domain is currently selected, if any */
  selectedRef: number | null;
  /* Whether to show domain type labels in the antibody viz */
  showLabels: boolean;
  /* Whether tho show the antibody minimap in the expanded preview */
  showMinimap: boolean;
}

export interface AntibodyMapStateParams {
  /* The state of the antibody map. */
  state: AntibodyMapState;
  /**
   * A function to be called when the domain preview is expanded or unexpanded.
   * @param expandPreview Whether the domain preview should be expanded.
   * @param x The x-coordinate of the top left corner of the draggable antibody viz. Does not change value if
   *          not passed.
   * @param y The y-coordinate of the top left corner of the draggable antibody viz. Does not change value if
   *          not passed.
   */
  setExpandPreview: (expandPreview: boolean, x?: number, y?: number) => void;
  /* A function to set which domain, if any, is hovered. */
  setHoveredRef: (hoveredRef: number | null) => void;
  /* A function to set which domain, if any, is selected. */
  setSelectedRef: (selectedRef: number | null) => void;
  /* A function to set whether to show domain type labels in the antibody viz. */
  setShowLabels: (showLabels: boolean) => void;
  /* A function to set whether the minimap is shown in the domain preview. */
  setShowMinimap: (showMinimap: boolean) => void;
  /**
   * A function to be called whenever a drag is initiated on the draggable antibody viz.
   * @param offsetX The x-coordinate of the click, relative to the bounding box.
   * @param offsetY The y-coordinate of the click, relative to the bounding box.
   */
  startDrag: (offsetX: number, offsetY: number) => void;
  /**
   * A function to be called whenever the draggable antibody viz is dropped.
   * @param x The x-coordinate of the top left corner of the draggable antibody viz.
   * @param y The y-coordinate of the top left corner of the draggable antibody viz.
   */
  endDrag: (x: number, y: number) => void;
}

/* @package */
export const DEFAULT_ANTIBODY_DRAG_COORDINATE = -1;

const DEFAULT_ANTIBODY_DRAG_STATE: AntibodyDragState = {
  x: DEFAULT_ANTIBODY_DRAG_COORDINATE,
  y: DEFAULT_ANTIBODY_DRAG_COORDINATE,
  offsetX: DEFAULT_ANTIBODY_DRAG_COORDINATE,
  offsetY: DEFAULT_ANTIBODY_DRAG_COORDINATE,
  isDragging: false,
};

/* @package */
export const DEFAULT_ANTIBODY_MAP_STATE: AntibodyMapState = {
  dragState: DEFAULT_ANTIBODY_DRAG_STATE,
  expandPreview: false,
  hoveredRef: null,
  selectedRef: null,
  showLabels: true,
  showMinimap: true,
};

/**
 * Exported for testing.
 * @package
 */
export const antibodyMapStateHook = makeUseGlobalClientState<AntibodyMapState>(DEFAULT_ANTIBODY_MAP_STATE);

export default function useAntibodyMapState(antibodyId: string): AntibodyMapStateParams {
  const [state, setState] = antibodyMapStateHook.useGlobalClientState(`antibody-interactivity-${antibodyId}`);
  return {
    state,
    setExpandPreview: (expandPreview: boolean, x?: number, y?: number): void => {
      setState({
        ...state,
        expandPreview,
        dragState: {...state.dragState, x: x ?? state.dragState.x, y: y ?? state.dragState.y},
      });
    },
    setHoveredRef: (hoveredRef: number | null): void => {
      setState({...state, hoveredRef});
    },
    setSelectedRef: (selectedRef: number | null): void => {
      setState({
        ...state,
        selectedRef,
        ...(selectedRef == null ? {expandPreview: false} : {}),
      });
    },
    setShowLabels: (showLabels: boolean): void => {
      setState({...state, showLabels});
    },
    setShowMinimap: (showMinimap: boolean): void => {
      setState({...state, showMinimap});
    },
    startDrag: (offsetX: number, offsetY: number): void => {
      setState({
        ...state,
        dragState: {...state.dragState, isDragging: true, offsetX, offsetY},
      });
    },
    endDrag: (x: number, y: number): void => {
      setState({
        ...state,
        dragState: {
          ...state.dragState,
          isDragging: false,
          x,
          y,
          offsetX: DEFAULT_ANTIBODY_DRAG_COORDINATE,
          offsetY: DEFAULT_ANTIBODY_DRAG_COORDINATE,
        },
      });
    },
  };
}
